import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Receipt, ArrowsClockwise, PaperPlaneTilt, CheckCircle, Warning, CurrencyInr } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const HR_ROLES = new Set(["super_admin", "company_admin", "hr"]);
const fmtINR = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

const STATUS_STYLE = {
  raised:  { bg: "bg-zenith-blue-500/10 border-zenith-blue-500/30 text-zenith-blue-700", label: "Raised" },
  sent:    { bg: "bg-zenith-teal/10 border-zenith-teal/40 text-zenith-teal-700", label: "Sent" },
  paid:    { bg: "bg-zenith-green/10 border-zenith-green/40 text-zenith-green", label: "Paid" },
  overdue: { bg: "bg-red-50 border-red-200 text-red-700", label: "Overdue" },
  draft:   { bg: "bg-zinc-100 border-zinc-200 text-zinc-600", label: "Draft" },
};

export default function InvoicingPipeline() {
  const { user } = useAuth();
  const isAdmin = HR_ROLES.has(user?.role);
  const [pipeline, setPipeline] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: p }, { data: inv }, { data: pj }] = await Promise.all([
        api.get("/pmo/client-invoices/pipeline", { params: { months: 6 } }),
        api.get("/pmo/client-invoices"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setPipeline(p); setInvoices(inv); setProjects(pj);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const chartData = useMemo(() => (pipeline?.months || []).map((m) => ({
    label: m.label,
    // Split "raised" into its non-overlapping buckets for stacked view
    "Not yet sent": Math.max(m.raised - m.sent - m.paid - m.overdue, 0),
    Sent: m.sent, Paid: m.paid, Overdue: m.overdue,
  })), [pipeline]);

  const markStatus = async (id, status) => {
    try {
      await api.patch(`/pmo/client-invoices/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const kp = pipeline?.kpis || {};

  return (
    <div className="space-y-6" data-testid="invoicing-pipeline-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">Client invoicing pipeline</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Cash flow in one glance.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Every client invoice from raised → sent → paid. Overdue counters auto-compute against each invoice's due date.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="inv-refresh">
            <ArrowsClockwise size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Refresh
          </Button>
          {isAdmin && <NewInvoiceDialog projects={projects} onCreated={load} />}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPICard label="Raised (6mo)" value={`₹${fmtINR(kp.raised)}`} tone="navy" icon={Receipt} />
        <KPICard label="Collected" value={`₹${fmtINR(kp.paid)}`} tone="green" icon={CheckCircle} />
        <KPICard label="Outstanding" value={`₹${fmtINR(kp.outstanding)}`} tone="teal" icon={CurrencyInr} />
        <KPICard label="Overdue" value={`₹${fmtINR(kp.overdue)}`} tone="magenta" icon={Warning} />
        <KPICard label="Collection %" value={`${kp.collection_percent || 0}%`} tone="green" />
      </div>

      {/* Chart */}
      <div className="border border-zinc-200 rounded-md bg-white p-4">
        <div className="font-display font-bold text-sm text-adb-navy mb-3">Invoice pipeline · last 6 months</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis dataKey="label" fontSize={11} />
            <YAxis fontSize={10} tickFormatter={(v) => `₹${fmtINR(v)}`} />
            <Tooltip formatter={(v) => `₹${fmtINR(v)}`} contentStyle={{ fontSize: 12, borderRadius: 2 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Not yet sent" stackId="s" fill="#93a5cf" radius={[0, 0, 0, 0]} />
            <Bar dataKey="Sent" stackId="s" fill="#12b3a8" radius={[0, 0, 0, 0]} />
            <Bar dataKey="Paid" stackId="s" fill="#33cc33" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Overdue" stackId="s" fill="#e6007a" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Invoice table */}
      <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200">
          <div className="font-display font-bold text-sm text-adb-navy">All invoices</div>
        </div>
        <table className="w-full text-sm" data-testid="invoicing-pipeline-table">
          <thead className="bg-zinc-50 text-left">
            <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
              <th className="py-2.5 px-3">Invoice #</th>
              <th className="py-2.5 px-3">Client</th>
              <th className="py-2.5 px-3">Period</th>
              <th className="py-2.5 px-3">Due</th>
              <th className="py-2.5 px-3 text-right">Amount</th>
              <th className="py-2.5 px-3">Status</th>
              <th className="py-2.5 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((i) => {
              const s = STATUS_STYLE[i.status] || STATUS_STYLE.raised;
              return (
                <tr key={i.id} className="border-t border-zinc-100" data-testid={`invoicing-row-${i.id}`}>
                  <td className="py-2.5 px-3 font-mono-plex text-xs">{i.invoice_no}</td>
                  <td className="py-2.5 px-3">{i.client_name}</td>
                  <td className="py-2.5 px-3 text-xs text-zinc-500">{i.period_start} → {i.period_end}</td>
                  <td className="py-2.5 px-3 text-xs">{i.due_date}</td>
                  <td className="py-2.5 px-3 text-right font-semibold">₹{fmtINR(i.amount_inr)}</td>
                  <td className="py-2.5 px-3">
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest border ${s.bg}`}>{s.label}</span>
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {isAdmin && i.status === "raised" && (
                      <Button size="sm" variant="outline" onClick={() => markStatus(i.id, "sent")} className="rounded-sm text-xs h-7" data-testid={`inv-send-${i.id}`}>
                        <PaperPlaneTilt size={11} className="mr-1" /> Mark sent
                      </Button>
                    )}
                    {isAdmin && (i.status === "sent" || i.status === "overdue") && (
                      <Button size="sm" onClick={() => markStatus(i.id, "paid")} className="rounded-sm text-xs h-7 bg-zenith-green hover:bg-zenith-green/80 text-white" data-testid={`inv-pay-${i.id}`}>
                        <CheckCircle size={11} className="mr-1" /> Mark paid
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {invoices.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-zinc-400 text-sm">No client invoices yet — click "+ Invoice" to raise one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPICard({ label, value, tone = "navy", icon: Icon }) {
  const t = {
    navy: "text-adb-navy border-adb-navy/20 bg-adb-navy/5",
    green: "text-zenith-green border-zenith-green/30 bg-zenith-green/5",
    teal: "text-zenith-teal border-zenith-teal/30 bg-zenith-teal/5",
    magenta: "text-zenith-magenta border-zenith-magenta/30 bg-zenith-magenta/5",
  }[tone];
  return (
    <div className={`border ${t} rounded-sm p-3`}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={13} weight="duotone" />}
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      </div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function NewInvoiceDialog({ projects, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const nextMonth = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    client_name: "", project_id: "", amount_inr: "",
    period_start: today, period_end: today, due_date: nextMonth, notes: "",
  });
  const submit = async () => {
    if (!f.client_name || !f.amount_inr) { toast.error("Client + amount required"); return; }
    try {
      await api.post("/pmo/client-invoices", { ...f, amount_inr: Number(f.amount_inr) });
      toast.success("Invoice raised"); setOpen(false); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-adb-navy hover:bg-adb-navy-800" data-testid="inv-new-btn"><Plus size={14} className="mr-1" /> Invoice</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="inv-new-dialog">
        <DialogHeader><DialogTitle className="font-display">Raise client invoice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Client name *</Label>
            <Input value={f.client_name} onChange={(e) => setF({ ...f, client_name: e.target.value })} placeholder="Acme Corp" className="rounded-sm mt-1" data-testid="inv-client" />
          </div>
          <div>
            <Label className="text-xs">Project</Label>
            <Select value={f.project_id || undefined} onValueChange={(v) => setF({ ...f, project_id: v })}>
              <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Period start</Label>
              <Input type="date" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs">Period end</Label>
              <Input type="date" value={f.period_end} onChange={(e) => setF({ ...f, period_end: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Amount (INR) *</Label>
              <Input type="number" min={1} value={f.amount_inr} onChange={(e) => setF({ ...f, amount_inr: e.target.value })} placeholder="50000" className="rounded-sm mt-1" data-testid="inv-amount" />
            </div>
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="inv-submit-btn">Raise invoice</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
